/**
 * It contains backend Apis for social media posting.
 * @author: Aysegul Yalcinkaya
 */

// Constant.
import {BE_URLS} from "@/constants/config.js";

// Modules in the application.
import axios from "axios";
import axiosProxy from "@/api/axios/axios_express_proxy";
// OpenAI calls now routed through Express proxy (no API key in frontend)
import axios_runwayml from "@/api/axios/axios_runwayml";
import axios_pika from "@/api/axios/axios_pika";


// URLs for Backend
const HOST = BE_URLS.BACKEND_PAYMENT_SERVER_URL;

// Express proxy base URL
const PROXY_HOST = process.env.VUE_APP_EXPRESS_PROXY_URL || 'http://localhost:3001';

const CREATE_POST_CONTENT = `/api/ai/chat-completions`;
const CREATE_POST_IMAGE = `/api/ai/image-generations`;

// Ayrshare calls go through Express backend proxy
const CREATE_POST = `${PROXY_HOST}/api/ayrshare/post`;
const POST_HISTORY = `${PROXY_HOST}/api/ayrshare/history`;
const POST_ANALYTICS = `${PROXY_HOST}/api/ayrshare/analytics`;
const AUTO_SCHEDULE_SET = `${PROXY_HOST}/api/ayrshare/auto-schedule`;
const AUTO_SCHEDULE_GET = `${PROXY_HOST}/api/ayrshare/auto-schedule`;
const AUTO_SCHEDULE_DELETE = AUTO_SCHEDULE_GET;

// Runway ML API endpoints - using backend proxy to avoid CORS
const RUNWAY_IMAGE_TO_VIDEO = `${PROXY_HOST}/api/runway/image-to-video`;
const RUNWAY_TASK_STATUS = `${PROXY_HOST}/api/runway/tasks`;

// Kling AI API endpoints (via AI/ML API) - also through backend proxy
const KLING_IMAGE_TO_VIDEO = `${PROXY_HOST}/api/kling/image-to-video`;
const KLING_MULTI_IMAGE_TO_VIDEO = `${PROXY_HOST}/api/kling/multi-image-to-video`;
const KLING_TASK_STATUS = `${PROXY_HOST}/api/kling/tasks`;

// Pika API endpoints - through backend proxy
const PIKA_IMAGE_TO_VIDEO = `${PROXY_HOST}/api/pika/image-to-video`;
const PIKA_TASK_STATUS = `${PROXY_HOST}/api/pika/tasks`;

// Mock mode disabled - using real backend proxy
const USE_MOCK_MODE = false;

/**
 * Utility: generate future dates based on recurrence
 */
function getNextDate(currentDate, repeatType, customInterval) {
  const next = new Date(currentDate);
  switch (repeatType) {
    case 'daily':
      next.setDate(next.getDate() + 1);
      break;
    case 'weekly':
      next.setDate(next.getDate() + 7);
      break;
    case 'monthly':
      next.setMonth(next.getMonth() + 1);
      break;
    case 'custom':
      if (customInterval && typeof customInterval.value === 'number') {
        if (customInterval.unit === 'hours') next.setHours(next.getHours() + customInterval.value);
        else if (customInterval.unit === 'days') next.setDate(next.getDate() + customInterval.value);
        else if (customInterval.unit === 'weeks') next.setDate(next.getDate() + (customInterval.value * 7));
      }
      break;
  }
  return next;
}

function generatePostDates(startDate, repeatType, repeatCount, customInterval, endDate) {
  const dates = [];
  let current = new Date(startDate);

  if (endDate) {
    const finalDate = new Date(endDate);
    while (current <= finalDate) {
      dates.push(new Date(current));
      current = getNextDate(current, repeatType, customInterval);
    }
  } else {
    const count = Math.max(1, parseInt(repeatCount || 1, 10));
    for (let i = 0; i < count; i++) {
      dates.push(new Date(current));
      if (i < count - 1) current = getNextDate(current, repeatType, customInterval);
    }
  }

  return dates;
}

function varyContent(originalContent, index) {
  const variations = [
    originalContent,
    `${originalContent} 🌟`,
    `✨ ${originalContent}`,
    `${originalContent} #${index}`,
    `📢 ${originalContent}`
  ];
  return variations[index % variations.length];
}
/**
 * Send request to create post
 * @return {Promise} POST Promise.
 */
export function createScheduledPost(data) {
    let requestData = {
        profileKey: data.profileKey,
        post: (data.post || data.content || '').toString(),
        platforms: data.platforms,
        scheduleDate: data.scheduleDate
    };

    // Only include mediaUrls if a valid URL is present; avoid sending an empty array
    if (data.mediaUrls) {
        requestData.mediaUrls = Array.isArray(data.mediaUrls) ? data.mediaUrls : [data.mediaUrls];
    }

    // Include Instagram-specific options (official Ayrshare API parameters)
    if (data.instagramOptions) {
        requestData.instagramOptions = data.instagramOptions;
    }
    
    // Include auto-schedule if present
    // Support two shapes:
    // 1) { autoSchedule: true, title: 'My schedule' }
    // 2) { autoSchedule: { schedule: true, title: 'My schedule' } }
    if (data.autoSchedule === true && typeof data.title === 'string') {
        requestData.autoSchedule = true;
        requestData.title = data.title;

        delete requestData.scheduleDate;
    } else if (data.autoSchedule && data.autoSchedule.schedule && data.autoSchedule.title) {
        requestData.autoSchedule = { schedule: true, title: data.autoSchedule.title };
        // Do not send scheduleDate when using autoSchedule
        delete requestData.scheduleDate;
    }

    // Only include auto-repost parameters if provided and valid (flattened as top-level keys).
    const autoRepost = data.autoRepost || data.autopost; // support both keys
    if (autoRepost && Object.keys(autoRepost).length > 0) {
        if (typeof autoRepost.repeat === 'number') requestData.repeat = autoRepost.repeat;
        if (typeof autoRepost.days === 'number') requestData.days = autoRepost.days;
        if (autoRepost.startDate) requestData.startDate = autoRepost.startDate;
        delete requestData.scheduleDate; // ensure scheduleDate is not sent when using auto-repost
    }

    // Debug payload (without auth headers)
    console.log('[Ayrshare] createScheduledPost payload:', JSON.stringify(requestData));

    let result = axiosProxy.post(CREATE_POST, requestData);
    console.log(result);
    return result;
}

/**
 * Create multiple individually scheduled posts (recurring) by issuing
 * multiple POST /post calls with distinct scheduleDate values.
 * This avoids auto-repost when custom recurrence is needed.
 */
export async function createRecurringPosts(params) {
  const {
    profileKey,
    content,
    platforms,
    mediaUrls,
    startDate,
    repeatType,       // 'daily' | 'weekly' | 'monthly' | 'custom'
    repeatCount,      // number of posts to schedule
    customInterval,   // { unit: 'hours'|'days'|'weeks', value: number }
    endDate           // optional end date
  } = params;

  const dates = generatePostDates(startDate, repeatType, repeatCount, customInterval, endDate);
  const results = [];

  for (let i = 0; i < dates.length; i++) {
    const payload = {
      profileKey,
      post: varyContent(content, i + 1),
      platforms,
      mediaUrls: Array.isArray(mediaUrls) ? mediaUrls : (mediaUrls ? [mediaUrls] : []),
      scheduleDate: dates[i].toISOString(),
    };

    try {
      const res = await axiosProxy.request({ url: CREATE_POST, method: 'POST', data: payload });
      results.push({
        scheduledDate: dates[i],
        id: res?.data?.id || res?.data?.postId || null,
        status: res?.data?.status || null,
        response: res?.data,
      });
    } catch (err) {
      results.push({ scheduledDate: dates[i], error: err?.response?.data || err?.message || err });
    }
  }

  return results;
}

export async function getPostHistory(data, fetchAnalytics = true) {
  console.log("🚀 [API] getPostHistory() called with:", data);

  try {
    // profileKey passed in request body/params — proxy handles the header

    // Build URL with optional platform parameter
    // Premium plans can use platform parameter to get ALL posts including manual ones
    let url = POST_HISTORY;
    const params = { profileKey: data.profileKey };
    if (data.platform) {
      params.platform = data.platform;
    }

    const result = await axiosProxy.get(url, { params });

    console.log("✅ [API] getPostHistory() success:", result);

    // Only fetch analytics if requested
    if (fetchAnalytics && result.data && result.data.history && result.data.history.length > 0) {
      await enrichPostsWithAnalytics(result.data.history, data.profileKey);
    }

    return result;
  } catch (error) {
    console.error("❌ [API] getPostHistory() failed:", error.response?.data || error);
    throw error;
  }
}

/**
 * Get analytics/engagement data for a specific post
 * @param {string} postId - The post ID to get analytics for
 * @param {string} profileKey - The Ayrshare profile key
 * @return {Promise} GET Promise.
 */
export async function getPostAnalytics(postId, profileKey) {
  console.log("📊 [API] getPostAnalytics() called for postId:", postId);
  
  try {
    // profileKey passed via body/params to proxy
    
    const result = await axiosProxy.get(`${POST_ANALYTICS}/${postId}`, {
      params: { profileKey },
    });
    
    console.log("✅ [API] getPostAnalytics() success:", result);
    return result;
  } catch (error) {
    // Suppress 404 errors (endpoint does not exist) and return null
    if (error?.response && error.response.status === 404) {
      return null;
    }
    // Only show notification for other errors
    console.error("❌ [API] getPostAnalytics() failed:", error.response?.data || error);
    throw error;
  }
}

/** Delete a post (scheduled/pending) by id or refId */
export async function deletePost({ profileKey, id, refId }) {
  const payload = { profileKey };
  if (id) payload.id = id;
  else if (refId) payload.refId = refId;
  else throw new Error('deletePost: id or refId required');
  return axiosProxy.delete(CREATE_POST, { data: payload });
}

// (delete scheduled post support removed)

/**
 * Enrich posts with analytics data
 * @param {Array} posts - Array of posts to enrich
 * @param {string} profileKey - The Ayrshare profile key
 */
export async function enrichPostsWithAnalytics(posts, profileKey) {
  console.log("🔍 [API] enrichPostsWithAnalytics() called for", posts.length, "posts");

  for (let post of posts) {
    if (post.postIds && Array.isArray(post.postIds)) {
      // Initialize analytics object for this post
      post.analytics = {
        totalLikes: 0,
        totalComments: 0,
        totalShares: 0,
        totalViews: 0,
        totalReach: 0,
        platformBreakdown: {}
      };

      // Debug: log status and postIds
      if (post.status !== 'success') {
        console.log(`[DEBUG] Skipping analytics for postId(s): ${JSON.stringify(post.postIds)} with status: ${post.status}`);
        continue;
      }

      // Track if post might be deleted (all analytics fail with 404)
      let allAnalyticsFailed = true;

      // Get analytics for each platform post
      for (let postId of post.postIds) {
        const id = typeof postId === 'object' && postId !== null ? postId.id : postId;
        console.log(`[DEBUG] Fetching analytics for postId: ${id} with status: ${post.status}`);
        if (!id) continue;
        try {
          const analyticsResult = await getPostAnalytics(id, profileKey);
          if (analyticsResult && analyticsResult.data) {
            allAnalyticsFailed = false; // At least one analytics call succeeded
            const analytics = analyticsResult.data;
            // Aggregate metrics across platforms
            post.analytics.totalLikes += analytics.likes || 0;
            post.analytics.totalComments += analytics.comments || 0;
            post.analytics.totalShares += analytics.shares || 0;
            post.analytics.totalViews += analytics.views || 0;
            post.analytics.totalReach += analytics.reach || 0;
            // Store platform-specific data
            if (analytics.platform) {
              post.analytics.platformBreakdown[analytics.platform] = {
                likes: analytics.likes || 0,
                comments: analytics.comments || 0,
                shares: analytics.shares || 0,
                views: analytics.views || 0,
                reach: analytics.reach || 0
              };
            }
          }
        } catch (error) {
          // Only warn if not a 404 (not found)
          if (!(error?.response && error.response.status === 404)) {
            console.warn(`⚠️ Could not get analytics for post ${id}:`, error);
            allAnalyticsFailed = false; // It's a real error, not a deleted post
          }
          // Otherwise, just skip (404 means post might be deleted)
        }
      }

      // Mark post as potentially deleted if all analytics failed with 404
      // AND the post is older than 24 hours (to avoid false positives for new posts)
      if (allAnalyticsFailed && post.postIds.length > 0) {
        const postAge = post.created ? (Date.now() - new Date(post.created).getTime()) : 0;
        const is24HoursOld = postAge > (24 * 60 * 60 * 1000);

        if (is24HoursOld) {
          post._possiblyDeleted = true;
          console.log(`⚠️ Post ${post.id} might be deleted (all analytics returned 404 and post is >24h old)`);
        } else {
          console.log(`ℹ️ Post ${post.id} has no analytics yet (post is <24h old, giving it time)`);
        }
      }
    }
  }

  console.log("✅ [API] enrichPostsWithAnalytics() completed");
}

/**
 * Auto Schedule APIs (Ayrshare)
 */
export async function createAutoSchedule({ profileKey, title, times, schedule, daysOfWeek, excludeDates }) {
  const payload = { profileKey, title, schedule: schedule || times, daysOfWeek, excludeDates };
  return axiosProxy.post(AUTO_SCHEDULE_SET, payload);
}

export async function getAutoSchedules({ profileKey }) {
  return axiosProxy.get(AUTO_SCHEDULE_GET, { params: { profileKey } });
}

/**
 * Upload an image for social media posting.
 * Returns a publicly accessible URL.
 */
export async function uploadSocialImage(file) {
  const formData = new FormData();
  formData.append('file', file);
  return axiosProxy.post('/api/upload/image', formData);
}

export async function deleteAutoSchedule({ profileKey, title }) {
  return axiosProxy.delete(AUTO_SCHEDULE_DELETE, { data: { profileKey, title } });
}

//createPostContent function
/**
 * Create reel content directly from a prompt
 * @param {Object} data - Object with prompt and maxTokens
 * @return {Promise} OpenAI response
 */
export async function createReelContent(data) {
  console.log('[createReelContent]', data);

  const { prompt, maxTokens = 150 } = data;

  return await axiosProxy.request({
    url: CREATE_POST_CONTENT,
    method: "POST",
    data: {
      model: "gpt-4o-mini",
      temperature: 0.9,
      top_p: 0.95,
      presence_penalty: 0.6,
      frequency_penalty: 0.8,
      max_tokens: maxTokens,
      messages: [
        {
          role: "system",
          content: "You are a social media content creator specializing in engaging reel content. Create concise, engaging content suitable for Instagram/Facebook reels."
        },
        {
          role: "user",
          content: prompt
        }
      ]
    }
  });
}

export async function createPostContent(data) {
  console.log(data);

  let messageContent = ""; // Initialize an empty string to hold the specific message content.
  let systemContent = "";

  // Determine the focus of the post and create a tailored message
  const perkStr = typeof data.perk === 'string' ? data.perk : '';
  const isGeneralStorePromo = perkStr.toLowerCase().includes('general store promo');
  const hasProductList = Array.isArray(data.products) && data.products.length > 0;

  if (isGeneralStorePromo) {
      // General store promotional copy
      messageContent = "Business type: " + data.businessType + "\n" +
          "Description: " + data.description + "\n" +
          "Name: " + data.name + "\n" +
          "Business lines: " + data.businessLines + "\n" +
          "Goal: Create a short, enticing promotion for this business with a clear call to action. Keep under 200 characters.";
      systemContent = "You are a helpful assistant to create post content to promote a business.\n" +
        "The business information is shown below. Create content with 200 characters max.\n" +
        "Do NOT include any dates or times.\n" +
        "Focus only on the store's offering and description with a clear, natural tone.";
  } else if (hasProductList) {
      // Specific product promotion with multiple products
      const productNames = data.products.join(', ');
      messageContent = "Business type: " + data.businessType + "\n" +
          "Description: " + data.description + "\n" +
          "Name: " + data.name + "\n" +
          "Business lines: " + data.businessLines + "\n" +
          "Promote products: " + productNames + "\n" +
          "Promotion start date: " + (data.startDate || '') + "\n" +
          "Goal: Create a short promo highlighting the listed product(s). Include an engaging CTA. Max 200 characters.";
      systemContent = "You are a helpful assistant to create post content for a specific product promotion.\n" +
        "Create content with 200 characters max. Emphasize the listed product names concisely with a clear call to action.\n" +
        "Avoid including dates or prices unless explicitly provided.";
  } else if (data.perk && !data.holiday) { // Perk or single product name
      messageContent = "Business type: " + data.businessType + "\n" +
          "Description: " + data.description + "\n" +
          "Name: " + data.name + "\n" +
          "Business lines: " + data.businessLines + "\n" +
          "Promotion: " + data.perk + "\n" +
          "Promotion start date: " + data.startDate;
      systemContent = "You are a helpful assistant to create post content for a single promotion.\n" +
        "Create content with 200 characters max and a natural, enticing tone.";
  } else if (data.holiday && !data.perk) { // Holiday
      messageContent = "Business type: " + data.businessType + "\n" +
          "Description: " + data.description + "\n" +
          "Name: " + data.name + "\n" +
          "Business lines: " + data.businessLines + "\n" +
          "Celebrating: " + data.holiday + "\n" +
          "Event date: " + data.startDate;
      systemContent = "You are a helpful assistant to create post content for a holiday-themed post.\n" +
        "Create content with 200 characters max. Keep it warm and relevant to the holiday.";
  } else {
      // Default
      messageContent = "Business type: " + data.businessType + "\n" +
          "Description: " + data.description + "\n" +
          "Name: " + data.name + "\n" +
          "Business lines: " + data.businessLines + "\n" +
          "Note: No specific promotion or holiday information provided.";
      systemContent = "You are a helpful assistant to create concise social media post content (≤200 chars) based on the provided business context.";
  }

  // Encourage distinct variation if provided
  if (data && typeof data.variationHint === 'string' && data.variationHint.trim().length > 0) {
    messageContent += "\n\nVariation instruction: " + data.variationHint.trim();
  }

  return await axiosProxy.request({
      url: CREATE_POST_CONTENT,
      method: "POST",
      data: {
          model: "gpt-4o-mini",
          temperature: 0.9,
          top_p: 0.95,
          presence_penalty: 0.6,
          frequency_penalty: 0.8,
          messages: [
              {
                  role: "system",
                  content: systemContent
              },
              {
                  role: "user",
                  content: messageContent
              }
          ]
      }
  });
}


export async function createPostImage(data) {
  let prompt = "";

  try {
    // Priority 1: Use custom prompt if provided (for AI-generated descriptions)
    if (data.prompt && typeof data.prompt === 'string' && data.prompt.trim() !== '') {
      prompt = data.prompt.trim();
      console.log('[createPostImage] Using custom prompt:', prompt);
    } else {
      // Priority 2: Generate business-specific prompts (original behavior)
      const businessType = (data.businessType || 'business').toLowerCase();
      const businessLines = Array.isArray(data.businessLines) ? data.businessLines.join(', ') : 'general business';

      // Use safe, generic prompts
      if (data.hasOwnProperty("perk") && data.perk) {
        prompt = `Create a professional business marketing image for a ${businessType}. ` +
                 `Clean, modern style with natural lighting and soft shadows. ` +
                 `No text in the image. Professional photography style.`;
      } else if (data.holiday) {
        prompt = `Create a festive business image for ${data.holiday}. ` +
                 `Clean, professional style with holiday theme. ` +
                 `No text in the image. Natural lighting.`;
      } else {
        prompt = `Create a professional business image for a ${businessType}. ` +
                 `Modern, clean aesthetic with natural lighting. ` +
                 `Professional marketing photography style. No text in the image.`;
      }
      console.log('[createPostImage] Using generated business prompt:', prompt);
    }

    return await axiosProxy.request({
        url: CREATE_POST_IMAGE,
        method: "POST",
        data: {
            model: "dall-e-3",
            prompt: prompt,
            style: "vivid",
            quality: "hd",
            response_format: "url",
            n: 1,
            size: "1024x1024"
        }
    });

  } catch (error) {
    // If the request fails due to safety issues, try an even more generic prompt
    if (error.response?.status === 400 && error.response?.data?.error?.message?.includes('safety')) {
      console.log('[OpenAI] Safety issue detected, trying generic prompt...');

      const fallbackPrompt = "Create a modern, professional business marketing image. " +
                           "Clean aesthetic with natural lighting and soft shadows. " +
                           "No text or words in the image. Commercial photography style.";

      return await axiosProxy.request({
          url: CREATE_POST_IMAGE,
          method: "POST",
          data: {
              model: "dall-e-3",
              prompt: fallbackPrompt,
              style: "vivid",
              quality: "hd",
              response_format: "url",
              n: 1,
              size: "1024x1024"
          }
      });
    }
    throw error;
  }


}

/**
 * Generate video clip from image using Runway ML
 * @param {string} imageUrl - URL of the image to animate
 * @param {Object} options - Video generation options
 * @return {Promise} POST Promise with task ID
 */
/**
 * Test Runway ML API key and connectivity
 * @return {Promise} Test response
 */
export async function testRunwayAPI() {
  if (USE_MOCK_MODE) {
    console.log('[Runway] Using mock mode due to CORS restrictions');
    return {
      status: 'MOCK_MODE',
      message: 'Mock mode enabled - CORS prevents direct browser calls to Runway ML API'
    };
  }

  try {
    // Test with backend proxy
    const response = await axiosProxy.request({
      url: RUNWAY_IMAGE_TO_VIDEO,
      method: "POST",
      data: {
        promptImage: "https://via.placeholder.com/1024x1024", // Dummy image for test
        promptText: "Test animation",
        duration: 5
      }
    });

    console.log('[Runway] Backend proxy test successful:', response.data);
    return response;
  } catch (error) {
    console.error('[Runway] API test failed:', error);
    console.error('[Runway] Error details:', error.response?.data || error.message);

    // If it's a CORS error, enable mock mode
    if (error.message.includes('CORS') || error.message.includes('Network Error')) {
      console.log('[Runway] CORS error detected, switching to mock mode');
      return {
        status: 'CORS_ERROR',
        message: 'CORS prevents direct API calls - need backend proxy for production'
      };
    }

    // If it's a 400 error, the API is working but our test data is bad - that's OK for testing
    if (error.response?.status === 400) {
      console.log('[Runway] API is working (400 means API accessible but bad test data)');
      return { status: 'API_WORKING', message: 'API accessible, credentials valid' };
    }

    throw error;
  }
}

export async function createVideoFromImage(imageUrl, options = {}) {
  const {
    duration = 10,
    motion = 5,
    seed = null,
    watermark = false
  } = options;

  // Mock mode for CORS testing
  if (USE_MOCK_MODE) {
    console.log('[Runway] Using mock mode - simulating video generation');

    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Return mock task response
    return {
      data: {
        id: 'mock_task_' + Date.now(),
        status: 'PENDING'
      }
    };
  }

  try {
    // Use backend proxy for video generation
    console.log('[Runway] Starting video generation via backend proxy');

    const response = await axiosProxy.request({
      url: RUNWAY_IMAGE_TO_VIDEO,
      method: "POST",
      data: {
        promptImage: imageUrl,
        promptText: "Animate this image with subtle motion and life",
        duration: duration,
        motion: motion
      }
    });

    console.log('[Runway] Video generation started:', response.data);
    return response;
  } catch (error) {
    console.error('[Runway] Video generation failed:', error);
    console.error('[Runway] Error details:', error.response?.data || error.message);

    // If CORS error, fall back to mock mode
    if (error.message.includes('CORS') || error.message.includes('Network Error')) {
      console.log('[Runway] CORS error, falling back to mock mode');
      return {
        data: {
          id: 'mock_task_cors_' + Date.now(),
          status: 'PENDING'
        }
      };
    }

    // More detailed error handling
    if (error.response?.status === 404) {
      throw new Error('Runway ML API endpoint not found. The API format may have changed.');
    } else if (error.response?.status === 401) {
      throw new Error('Invalid Runway ML API key. Please verify your API key.');
    } else if (error.response?.status === 400) {
      throw new Error(`Invalid request format: ${JSON.stringify(error.response?.data) || 'Bad request'}`);
    } else if (error.response?.status === 402) {
      throw new Error('Insufficient credits or billing issue with Runway ML account.');
    }

    throw error;
  }
}

/**
 * Create video from image using Kling AI
 * @param {string} imageUrl - The image URL to animate
 * @param {Object} options - Generation options
 * @return {Promise} POST Promise with task response
 */
export async function createVideoFromImageKling(imageUrl, options = {}) {
  const {
    duration = 10,
    prompt = "Animate this image with smooth, realistic motion",
    model = "kling-video/v2.1/standard/image-to-video"
  } = options;

  try {
    console.log('[Kling] Starting video generation via backend proxy');
    const response = await axiosProxy.request({
      url: KLING_IMAGE_TO_VIDEO,
      method: "POST",
      data: {
        image_url: imageUrl,
        prompt: prompt,
        model: model,
        duration: duration
      }
    });

    console.log('[Kling] Video generation started:', response.data);
    return response;
  } catch (error) {
    console.error('[Kling] Video generation failed:', error);
    throw error;
  }
}

/**
 * Check the status of a Kling AI video generation task
 * @param {string} taskId - The task ID from createVideoFromImageKling
 * @return {Promise} GET Promise with task status
 */
export async function getKlingTaskStatus(taskId) {
  try {
    const response = await axiosProxy.request({
      url: `${KLING_TASK_STATUS}/${taskId}`,
      method: "GET"
    });

    console.log('[Kling] Task status:', response.data);
    return response;
  } catch (error) {
    console.error('[Kling] Failed to get task status:', error);
    throw error;
  }
}

/**
 * Create video from image using Pika API
 * @param {string} imageUrl - The image URL to animate
 * @param {Object} options - Generation options
 * @return {Promise} POST Promise with task response
 */
export async function createVideoFromImagePika(imageUrl, options = {}) {
  const {
    duration = 3,
    prompt = "Animate this image with smooth, cinematic motion and professional quality",
    aspectRatio = "16:9",
    motionStrength = 3
  } = options;

  try {
    console.log('[Pika] Starting video generation via backend proxy');
    const response = await axiosProxy.request({
      url: PIKA_IMAGE_TO_VIDEO,
      method: "POST",
      data: {
        image_url: imageUrl,
        prompt: prompt,
        duration: duration,
        aspect_ratio: aspectRatio,
        motion_strength: motionStrength
      }
    });

    console.log('[Pika] Video generation started:', response.data);
    return response;
  } catch (error) {
    console.error('[Pika] Video generation failed:', error);
    throw error;
  }
}

/**
 * Check the status of a Pika video generation task
 * @param {string} taskId - The task ID from createVideoFromImagePika
 * @return {Promise} GET Promise with task status
 */
export async function getPikaTaskStatus(taskId) {
  try {
    const response = await axiosProxy.request({
      url: `${PIKA_TASK_STATUS}/${taskId}`,
      method: "GET"
    });

    console.log('[Pika] Task status:', response.data);
    return response;
  } catch (error) {
    console.error('[Pika] Failed to get task status:', error);
    throw error;
  }
}

/**
 * Check the status of a Runway ML video generation task
 * @param {string} taskId - The task ID from createVideoFromImage
 * @return {Promise} GET Promise with task status
 */
export async function getVideoTaskStatus(taskId) {
  // Mock mode for CORS testing
  if (USE_MOCK_MODE || taskId.startsWith('mock_task')) {
    console.log('[Runway] Using mock mode - simulating task completion');

    // Simulate processing time
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Return mock completed task with sample video
    return {
      data: {
        id: taskId,
        status: 'SUCCEEDED',
        output: 'https://sample-videos.com/zip/10/mp4/480/sample_640x360.mp4' // Sample video URL
      }
    };
  }

  try {
    const response = await axiosProxy.request({
      url: `${RUNWAY_TASK_STATUS}/${taskId}`,
      method: "GET"
    });

    console.log('[Runway] Task status:', response.data);
    return response;
  } catch (error) {
    console.error('[Runway] Failed to get task status:', error);

    // If CORS error, return mock success
    if (error.message.includes('CORS') || error.message.includes('Network Error')) {
      console.log('[Runway] CORS error, returning mock completion');
      return {
        data: {
          id: taskId,
          status: 'SUCCEEDED',
          output: 'https://sample-videos.com/zip/10/mp4/480/sample_640x360.mp4'
        }
      };
    }

    throw error;
  }
}

/**
 * Generate multiple video clips from images and wait for completion
 * @param {Array} imageUrls - Array of image URLs
 * @param {Object} options - Video generation options
 * @return {Promise} Array of completed video URLs
 */
export async function generateVideoClips(imageUrls, options = {}) {
  console.log('[Video Generation] Starting batch processing for', imageUrls.length, 'images');

  const videoClips = [];
  const tasks = [];

  try {
    // Step 1: Start all video generation tasks
    for (let i = 0; i < imageUrls.length; i++) {
      const imageUrl = imageUrls[i];
      console.log(`[Video Generation] Starting task ${i + 1}/${imageUrls.length} for image:`, imageUrl);

      const response = await createVideoFromImage(imageUrl, options);
      tasks.push({
        taskId: response.data.id,
        imageUrl: imageUrl,
        index: i
      });

      // Add delay between requests to avoid rate limiting
      if (i < imageUrls.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }

    console.log('[Video Generation] All tasks started, waiting for completion...');

    // Step 2: Poll for completion of all tasks
    const maxAttempts = 60; // 10 minutes max wait time
    const pollInterval = 10000; // 10 seconds

    for (const task of tasks) {
      let attempts = 0;
      let completed = false;

      while (!completed && attempts < maxAttempts) {
        attempts++;
        console.log(`[Video Generation] Polling task ${task.index + 1}, attempt ${attempts}/${maxAttempts}`);

        const statusResponse = await getVideoTaskStatus(task.taskId);
        const status = statusResponse.data.status;

        if (status === 'SUCCEEDED') {
          const videoUrl = statusResponse.data.output?.[0] || statusResponse.data.output;
          console.log(`[Video Generation] Task ${task.index + 1} completed:`, videoUrl);

          videoClips[task.index] = {
            originalImage: task.imageUrl,
            videoUrl: videoUrl,
            taskId: task.taskId
          };
          completed = true;
        } else if (status === 'FAILED') {
          console.error(`[Video Generation] Task ${task.index + 1} failed:`, statusResponse.data);
          throw new Error(`Video generation failed for image ${task.index + 1}: ${statusResponse.data.failure_reason || 'Unknown error'}`);
        } else {
          // Still processing, wait before next poll
          console.log(`[Video Generation] Task ${task.index + 1} status: ${status}, waiting...`);
          await new Promise(resolve => setTimeout(resolve, pollInterval));
        }
      }

      if (!completed) {
        throw new Error(`Video generation timed out for image ${task.index + 1}`);
      }
    }

    console.log('[Video Generation] All video clips generated successfully:', videoClips);
    return videoClips;

  } catch (error) {
    console.error('[Video Generation] Batch processing failed:', error);
    throw error;
  }
}

/**
 * Compile video clips into final reel using backend FFmpeg service
 * @param {Array} videoClips - Array of video clip objects
 * @param {Object} options - Compilation options
 * @return {Promise} Final compiled video URL
 */
export async function compileVideoReel(videoClips, options = {}) {
  const {
    transitions = true,
    transitionDuration = 0.5,
    audioTrack = null,
    outputFormat = 'mp4',
    resolution = '1080x1920' // Portrait for reels
  } = options;

  console.log('[Video Compilation] Starting compilation of', videoClips.length, 'clips');

  try {
    // This would typically call a backend service that uses FFmpeg
    // For now, we'll create a mock implementation
    const response = await axios.request({
      url: `${HOST}/video/compile-reel`,
      method: "POST",
      data: {
        videoClips: videoClips.map(clip => ({
          url: clip.videoUrl,
          duration: 10
        })),
        transitions: transitions,
        transitionDuration: transitionDuration,
        audioTrack: audioTrack,
        outputFormat: outputFormat,
        resolution: resolution
      }
    });

    console.log('[Video Compilation] Compilation completed:', response.data);
    return response.data.compiledVideoUrl;

  } catch (error) {
    console.error('[Video Compilation] Failed:', error);
    throw error;
  }
}

/**
 * Multi-platform video generation: Create single video using selected platform
 * @param {Array} imageUrls - Array of generated image URLs
 * @param {Object} options - Generation options including platform
 * @return {Promise} Object with video URL and metadata
 */
/**
 * Generate enhanced motion prompt based on story context and scene
 * @param {Object} options - Generation options with story context
 * @return {string} Enhanced motion prompt
 */
function generateEnhancedMotionPrompt(options = {}) {
  const { storyPrompt, useEnhancedMotion, sceneNumber = 1 } = options;

  if (!useEnhancedMotion) {
    return "Animate this image with smooth, realistic motion and natural movement";
  }

  // Base motion types for engaging videos
  const motionTypes = [
    "Slow cinematic camera zoom in, with subtle character movement and environmental details coming to life",
    "Gentle camera pan across the scene, with natural lighting changes and atmospheric movement",
    "Smooth dolly camera movement, with characters showing subtle expressions and gestures",
    "Elegant camera tilt and slight rotation, with dynamic lighting and shadow movement",
    "Professional tracking shot, with realistic character interactions and background activity"
  ];

  // Story-specific motion enhancements
  const storyMotions = {
    coffee: "Steam rises from coffee cups, people naturally interact, warm lighting flickers gently",
    restaurant: "Gentle bustle of restaurant activity, warm lighting, natural customer movements",
    bakery: "Flour gently settles, ovens glow warmly, bakers move naturally in background",
    shop: "Customers browse naturally, products catch light beautifully, welcoming atmosphere",
    office: "Professional yet dynamic environment, natural work interactions, modern lighting",
    workout: "Energetic movement, dynamic poses, motivating atmosphere with flowing motion",
    outdoor: "Natural breeze effects, organic environmental movement, beautiful natural lighting"
  };

  let enhancedPrompt = motionTypes[sceneNumber % motionTypes.length];

  // Add story-specific enhancements
  if (storyPrompt) {
    const storyLower = storyPrompt.toLowerCase();
    for (const [keyword, motion] of Object.entries(storyMotions)) {
      if (storyLower.includes(keyword)) {
        enhancedPrompt += `. ${motion}`;
        break;
      }
    }
  }

  // Add cinematic quality instructions
  enhancedPrompt += ". Professional commercial quality, smooth motion, no camera shake, elegant transitions, perfect timing, Instagram reel optimized";

  return enhancedPrompt;
}

export async function generateVideoWithPlatform(imageUrls, options = {}) {
  const { platform = 'runway' } = options;

  console.log(`[Multi-Platform Video] Creating video with ${platform.toUpperCase()}`);

  // Generate enhanced motion prompt if requested
  if (options.useEnhancedMotion) {
    options.prompt = generateEnhancedMotionPrompt(options);
    console.log(`[Multi-Platform Video] Using enhanced motion prompt: ${options.prompt}`);
  }

  if (platform === 'kling') {
    return await generateSimpleVideoReelKling(imageUrls, options);
  } else if (platform === 'pika') {
    return await generateSimpleVideoReelPika(imageUrls, options);
  } else {
    return await generateSimpleVideoReel(imageUrls, options);
  }
}

/**
 * Simple video generation with Pika: Create single video from best image
 * @param {Array} imageUrls - Array of generated image URLs
 * @param {Object} options - Generation options
 * @return {Promise} Object with video URL and metadata
 */
export async function generateSimpleVideoReelPika(imageUrls, options = {}) {
  console.log('[Pika Video Generation] Creating single video from', imageUrls.length, 'images');
  if (imageUrls.length === 0) {
    throw new Error('No images provided for video generation');
  }

  try {
    // Use the first image (you can add logic to pick the "best" one)
    const selectedImage = imageUrls[0];
    console.log('[Pika Video Generation] Using image:', selectedImage);

    // Generate single video from selected image
    const videoOptions = {
      duration: options.duration || 3,
      prompt: options.prompt || "Animate this image with smooth, cinematic motion and professional quality",
      aspectRatio: options.aspectRatio || "16:9",
      motionStrength: options.motionStrength || 3
    };

    console.log('[Pika Video Generation] Starting Pika AI generation...');
    const taskResponse = await createVideoFromImagePika(selectedImage, videoOptions);
    const taskId = taskResponse.data.id || taskResponse.data.task_id;
    console.log('[Pika Video Generation] Task started, ID:', taskId);

    // Poll for completion
    const maxAttempts = 60; // 10 minutes max
    const pollInterval = 10000; // 10 seconds
    let attempts = 0;
    let completed = false;
    let videoUrl = null;

    while (!completed && attempts < maxAttempts) {
      attempts++;
      console.log(`[Pika Video Generation] Polling attempt ${attempts}/${maxAttempts}`);
      const statusResponse = await getPikaTaskStatus(taskId);
      const status = statusResponse.data.status;

      if (status === 'success' || status === 'SUCCEEDED' || status === 'completed') {
        videoUrl = statusResponse.data.video_url || statusResponse.data.output || statusResponse.data.result?.video_url;
        console.log('[Pika Video Generation] Video completed:', videoUrl);
        completed = true;
      } else if (status === 'failed' || status === 'FAILED' || status === 'ERROR') {
        console.error('[Pika Video Generation] Task failed:', statusResponse.data);
        throw new Error(`Pika video generation failed: ${statusResponse.data.error || 'Unknown error'}`);
      } else {
        console.log(`[Pika Video Generation] Status: ${status}, waiting...`);
        await new Promise(resolve => setTimeout(resolve, pollInterval));
      }
    }

    if (!completed) {
      throw new Error('Pika video generation timed out');
    }

    const result = {
      finalVideoUrl: videoUrl,
      videoClips: [{ url: videoUrl, imageSource: selectedImage }],
      metadata: {
        platform: 'pika',
        duration: videoOptions.duration,
        prompt: videoOptions.prompt,
        aspectRatio: videoOptions.aspectRatio,
        motionStrength: videoOptions.motionStrength,
        sourceImage: selectedImage
      }
    };

    console.log('[Pika Video Generation] Generation completed:', result);
    return result;
  } catch (error) {
    console.error('[Pika Video Generation] Failed:', error);
    throw error;
  }
}

/**
 * Simple video generation with Kling AI: Create single video from best image
 * @param {Array} imageUrls - Array of generated image URLs
 * @param {Object} options - Generation options
 * @return {Promise} Object with video URL and metadata
 */
export async function generateSimpleVideoReelKling(imageUrls, options = {}) {
  console.log('[Kling Video Generation] Creating single video from', imageUrls.length, 'images');
  if (imageUrls.length === 0) {
    throw new Error('No images provided for video generation');
  }

  try {
    // Use the first image (you can add logic to pick the "best" one)
    const selectedImage = imageUrls[0];
    console.log('[Kling Video Generation] Using image:', selectedImage);

    // Generate single video from selected image
    const videoOptions = {
      duration: options.duration || 10,
      prompt: options.prompt || "Animate this image with smooth, realistic motion and natural movement",
      model: options.model || "kling-video/v2.1/standard/image-to-video"
    };

    console.log('[Kling Video Generation] Starting Kling AI generation...');
    const taskResponse = await createVideoFromImageKling(selectedImage, videoOptions);
    const taskId = taskResponse.data.data?.task_id;
    console.log('[Kling Video Generation] Task started, ID:', taskId);

    // Poll for completion
    const maxAttempts = 60; // 10 minutes max
    const pollInterval = 10000; // 10 seconds
    let attempts = 0;
    let completed = false;
    let videoUrl = null;

    while (!completed && attempts < maxAttempts) {
      attempts++;
      console.log(`[Kling Video Generation] Polling attempt ${attempts}/${maxAttempts}`);
      const statusResponse = await getKlingTaskStatus(taskId);
      const taskData = statusResponse.data.data || statusResponse.data;
      const status = taskData.task_status || statusResponse.data.status;

      if (status === 'succeed' || status === 'SUCCEEDED' || status === 'COMPLETED') {
        videoUrl = taskData.task_result?.videos?.[0]?.url || taskData.output || statusResponse.data.video_url;
        console.log('[Kling Video Generation] Video completed:', videoUrl);
        completed = true;
      } else if (status === 'failed' || status === 'FAILED' || status === 'ERROR') {
        console.error('[Kling Video Generation] Task failed:', statusResponse.data);
        throw new Error(`Kling video generation failed: ${taskData.error_message || statusResponse.data.error_message || 'Unknown error'}`);
      } else {
        console.log(`[Kling Video Generation] Status: ${status}, waiting...`);
        await new Promise(resolve => setTimeout(resolve, pollInterval));
      }
    }

    if (!completed) {
      throw new Error('Kling video generation timed out');
    }

    const result = {
      finalVideoUrl: videoUrl,
      videoClips: [{ url: videoUrl, imageSource: selectedImage }],
      metadata: {
        platform: 'kling',
        duration: videoOptions.duration,
        prompt: videoOptions.prompt,
        model: videoOptions.model,
        sourceImage: selectedImage
      }
    };

    console.log('[Kling Video Generation] Generation completed:', result);
    return result;
  } catch (error) {
    console.error('[Kling Video Generation] Failed:', error);
    throw error;
  }
}

/**
 * Multi-image video generation with Kling AI: Create video from multiple images
 * @param {Array} imageUrls - Array of generated image URLs (2-4 images)
 * @param {Object} options - Generation options
 * @return {Promise} Object with video URL and metadata
 */
export async function generateMultiImageVideoKling(imageUrls, options = {}) {
  console.log('[Kling Multi-Image] Creating video from', imageUrls.length, 'images');

  if (imageUrls.length < 2) {
    throw new Error('At least 2 images required for multi-image video generation');
  }

  if (imageUrls.length > 4) {
    console.log('[Kling Multi-Image] Too many images, using first 4');
    imageUrls = imageUrls.slice(0, 4);
  }

  try {
    console.log('[Kling Multi-Image] Using images:', imageUrls);

    // Generate video from multiple images
    const videoOptions = {
      duration: options.duration || "10",
      prompt: options.prompt || "Create a smooth video transitioning between these story images with natural flow, cinematic quality, and professional storytelling",
      aspect_ratio: options.aspect_ratio || "16:9"
    };

    console.log('[Kling Multi-Image] Starting multi-image generation with options:', videoOptions);

    // Call backend proxy endpoint
    const taskResponse = await axiosProxy.post(KLING_MULTI_IMAGE_TO_VIDEO, {
      image_urls: imageUrls,
      prompt: videoOptions.prompt,
      duration: videoOptions.duration,
      aspect_ratio: videoOptions.aspect_ratio
    });

    const taskId = taskResponse.data.data?.task_id;
    console.log('[Kling Multi-Image] Task started, ID:', taskId);

    // Poll for completion
    const maxAttempts = 60; // 10 minutes max
    const pollInterval = 10000; // 10 seconds
    let attempts = 0;
    let completed = false;
    let videoUrl = null;

    while (!completed && attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, pollInterval));
      attempts++;

      try {
        const statusResponse = await axiosProxy.get(`${KLING_TASK_STATUS}/${taskId}`);
        const status = statusResponse.data.data?.task_status;

        console.log(`[Kling Multi-Image] Attempt ${attempts}: Status ${status}`);

        if (status === 'succeed') {
          videoUrl = statusResponse.data.data?.task_result?.videos?.[0]?.url;
          completed = true;
        } else if (status === 'failed') {
          throw new Error('Kling multi-image video generation failed');
        }
      } catch (statusError) {
        console.error('[Kling Multi-Image] Status check failed:', statusError);
        if (attempts >= maxAttempts) throw statusError;
      }
    }

    if (!completed) {
      throw new Error('Kling multi-image video generation timed out');
    }

    const result = {
      finalVideoUrl: videoUrl,
      videoClips: [{ url: videoUrl, imageSource: imageUrls }],
      metadata: {
        platform: 'kling-multi',
        duration: videoOptions.duration,
        prompt: videoOptions.prompt,
        sourceImages: imageUrls,
        imageCount: imageUrls.length,
        type: 'multi-image-sequence'
      }
    };

    console.log('[Kling Multi-Image] Generation completed:', result);
    return result;
  } catch (error) {
    console.error('[Kling Multi-Image] Failed:', error);
    throw error;
  }
}

/**
 * Multi-image video splicing with Kling AI: Create multiple video segments from each image
 * @param {Array} imageUrls - Array of generated image URLs (2-8 images)
 * @param {Object} options - Generation options
 * @return {Promise} Object with individual video URLs and metadata
 */
export async function generateMultiImageVideoSplice(imageUrls, options = {}) {
  console.log('[Kling Splice] Creating spliced video from', imageUrls.length, 'images');

  if (imageUrls.length < 2) {
    throw new Error('At least 2 images required for multi-image video splicing');
  }

  // Limit to max 6 images for optimal performance and rate limiting
  if (imageUrls.length > 6) {
    console.log('[Kling Splice] Too many images, using first 6 to avoid rate limits');
    imageUrls = imageUrls.slice(0, 6);
  }

  try {
    console.log('[Kling Splice] Using images:', imageUrls);

    // Start splicing process - creates individual videos for each image
    const spliceOptions = {
      duration_per_image: options.durationPerImage || "3",
      prompt: options.prompt || "Create a smooth cinematic video segment with professional storytelling",
      aspect_ratio: options.aspect_ratio || "16:9"
    };

    console.log('[Kling Splice] Starting splice generation with options:', spliceOptions);

    // Call backend proxy endpoint to create all video tasks
    const taskResponse = await axiosProxy.post(`${PROXY_HOST}/api/kling/multi-image-video-splice`, {
      image_urls: imageUrls,
      prompt: spliceOptions.prompt,
      duration_per_image: spliceOptions.duration_per_image,
      aspect_ratio: spliceOptions.aspect_ratio
    });

    const videoTasks = taskResponse.data.videoTasks;
    console.log('[Kling Splice] Created', videoTasks.length, 'video tasks');

    // Poll for completion of all video segments
    const maxAttempts = 120; // 20 minutes max (longer for multiple videos)
    const pollInterval = 10000; // 10 seconds
    let attempts = 0;
    let allCompleted = false;
    let completedVideos = [];

    while (!allCompleted && attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, pollInterval));
      attempts++;

      try {
        // Check status of all video tasks
        const statusResponse = await axiosProxy.post(`${PROXY_HOST}/api/kling/check-splice-status`, {
          videoTasks: videoTasks
        });

        const { completed, pending, failed, isComplete } = statusResponse.data;

        console.log(`[Kling Splice] Attempt ${attempts}: ${completed.length}/${videoTasks.length} completed, ${pending.length} pending, ${failed.length} failed`);

        completedVideos = completed;

        if (isComplete) {
          allCompleted = true;
        } else if (failed.length > 0 && pending.length === 0) {
          // All remaining tasks have failed, proceed with any completed segments
          console.warn('[Kling Splice] Some video segments failed:', failed);
          if (completed.length > 0) {
            console.log('[Kling Splice] Proceeding with', completed.length, 'successful segments');
            allCompleted = true;
          }
        }

      } catch (statusError) {
        console.error('[Kling Splice] Status check failed:', statusError);
        if (attempts >= maxAttempts) throw statusError;
      }
    }

    if (completedVideos.length === 0) {
      throw new Error('Kling video splicing failed - no segments completed');
    }

    // Sort video segments by image index to maintain story order
    completedVideos.sort((a, b) => a.imageIndex - b.imageIndex);

    const result = {
      videoSegments: completedVideos.map(video => ({
        url: video.videoUrl,
        imageIndex: video.imageIndex,
        sourceImage: imageUrls[video.imageIndex]
      })),
      metadata: {
        platform: 'kling-splice',
        totalSegments: completedVideos.length,
        requestedSegments: videoTasks.length,
        durationPerSegment: spliceOptions.duration_per_image,
        totalDuration: (parseInt(spliceOptions.duration_per_image) * completedVideos.length) + ' seconds',
        prompt: spliceOptions.prompt,
        sourceImages: imageUrls,
        type: 'multi-image-splice',
        successRate: `${completedVideos.length}/${videoTasks.length}`
      }
    };

    console.log('[Kling Splice] Splicing completed:', result);
    return result;
  } catch (error) {
    console.error('[Kling Splice] Failed:', error);
    throw error;
  }
}

/**
 * Simple video generation: Create single video from best image using Runway ML
 * @param {Array} imageUrls - Array of generated image URLs
 * @param {Object} options - Generation options
 * @return {Promise} Object with video URL and metadata
 */
export async function generateSimpleVideoReel(imageUrls, options = {}) {
  console.log('[Simple Video Generation] Creating single video from', imageUrls.length, 'images');

  if (imageUrls.length === 0) {
    throw new Error('No images provided for video generation');
  }

  try {
    // Use the first image (you can add logic to pick the "best" one)
    const selectedImage = imageUrls[0];
    console.log('[Simple Video Generation] Using image:', selectedImage);

    // Generate single video from selected image
    const videoOptions = {
      duration: options.duration || 10,
      motion: options.motion || 5,
      seed: options.seed || null,
      watermark: false
    };

    console.log('[Simple Video Generation] Starting Runway ML generation...');
    const taskResponse = await createVideoFromImage(selectedImage, videoOptions);
    const taskId = taskResponse.data.id;

    console.log('[Simple Video Generation] Task started, ID:', taskId);

    // Poll for completion
    const maxAttempts = 60; // 10 minutes max
    const pollInterval = 10000; // 10 seconds
    let attempts = 0;
    let completed = false;
    let videoUrl = null;

    while (!completed && attempts < maxAttempts) {
      attempts++;
      console.log(`[Simple Video Generation] Polling attempt ${attempts}/${maxAttempts}`);

      const statusResponse = await getVideoTaskStatus(taskId);
      const status = statusResponse.data.status;

      if (status === 'SUCCEEDED') {
        videoUrl = statusResponse.data.output?.[0] || statusResponse.data.output;
        console.log('[Simple Video Generation] Video completed:', videoUrl);
        completed = true;
      } else if (status === 'FAILED') {
        console.error('[Simple Video Generation] Task failed:', statusResponse.data);
        throw new Error(`Video generation failed: ${statusResponse.data.failure_reason || 'Unknown error'}`);
      } else {
        console.log(`[Simple Video Generation] Status: ${status}, waiting...`);
        await new Promise(resolve => setTimeout(resolve, pollInterval));
      }
    }

    if (!completed) {
      throw new Error('Video generation timed out after 10 minutes');
    }

    const result = {
      finalVideoUrl: videoUrl,
      videoClips: [{
        originalImage: selectedImage,
        videoUrl: videoUrl,
        taskId: taskId
      }],
      metadata: {
        totalDuration: options.duration || 10,
        clipCount: 1,
        resolution: '1080x1920',
        format: 'mp4',
        sourceImage: selectedImage
      }
    };

    console.log('[Simple Video Generation] Generation completed:', result);
    return result;

  } catch (error) {
    console.error('[Simple Video Generation] Failed:', error);
    throw error;
  }
}

/**
 * Complete reel generation workflow: Images → Video Clips → Final Reel
 * @param {Array} imageUrls - Array of generated image URLs
 * @param {Object} options - Generation options
 * @return {Promise} Object with final video URL and metadata
 */
export async function generateCompleteVideoReel(imageUrls, options = {}) {
  console.log('[Reel Generation] Starting complete workflow for', imageUrls.length, 'images');

  try {
    // Step 1: Generate individual video clips from images
    const videoClips = await generateVideoClips(imageUrls, {
      duration: options.clipDuration || 10,
      motion: options.motion || 5
    });

    // Step 2: Compile clips into final reel
    const finalVideoUrl = await compileVideoReel(videoClips, {
      transitions: options.transitions !== false,
      transitionDuration: options.transitionDuration || 0.5,
      audioTrack: options.audioTrack
    });

    const result = {
      finalVideoUrl: finalVideoUrl,
      videoClips: videoClips,
      metadata: {
        totalDuration: videoClips.length * (options.clipDuration || 10),
        clipCount: videoClips.length,
        resolution: '1080x1920',
        format: 'mp4'
      }
    };

    console.log('[Reel Generation] Complete workflow finished:', result);
    return result;

  } catch (error) {
    console.error('[Reel Generation] Workflow failed:', error);
    throw error;
  }
}

/**
 * Video stitching using external service approach
 * Three implementation options are provided below
 */

// Option 1: Use Cloudinary for video concatenation (RECOMMENDED)
export async function stitchVideosCloudinary(videoUrls, options = {}) {
  console.log('[Cloudinary Stitching] Combining', videoUrls.length, 'videos');

  // This would require Cloudinary API setup
  // Example implementation:
  // 1. Upload all videos to Cloudinary
  // 2. Use Cloudinary's video concatenation API
  // 3. Return the concatenated video URL

  throw new Error('Cloudinary stitching not yet configured. Please add your Cloudinary credentials.');
}

// Option 2: Use FFmpeg.wasm (Browser-based FFmpeg) - TEMPORARILY DISABLED DUE TO WEBPACK COMPATIBILITY
export async function stitchVideosFFmpegWasm(videoUrls, options = {}) {
  console.log('[FFmpeg.wasm] Browser-based video stitching temporarily disabled due to Webpack compatibility issues');
  throw new Error('FFmpeg.wasm is temporarily disabled. Please use backend video processing or upgrade Webpack/Babel.');

  /* COMMENTED OUT UNTIL WEBPACK/BABEL UPGRADE
  if (videoUrls.length < 2) {
    throw new Error('At least 2 videos required for stitching');
  }

  try {
    // Dynamic import for FFmpeg.wasm
    const { FFmpeg } = await import('@ffmpeg/ffmpeg');
    const { fetchFile, toBlobURL } = await import('@ffmpeg/util');

    const ffmpeg = new FFmpeg();

    // Load FFmpeg.wasm
    console.log('[FFmpeg.wasm] Loading FFmpeg...');
    await ffmpeg.load({
      coreURL: await toBlobURL('/node_modules/@ffmpeg/core/dist/umd/ffmpeg-core.js', 'text/javascript'),
      wasmURL: await toBlobURL('/node_modules/@ffmpeg/core/dist/umd/ffmpeg-core.wasm', 'application/wasm'),
    });

    console.log('[FFmpeg.wasm] FFmpeg loaded successfully');

    // Download and write video files to FFmpeg filesystem
    const inputFiles = [];
    for (let i = 0; i < videoUrls.length; i++) {
      const filename = `input${i}.mp4`;
      console.log(`[FFmpeg.wasm] Downloading video ${i + 1}/${videoUrls.length}...`);

      const videoData = await fetchFile(videoUrls[i]);
      await ffmpeg.writeFile(filename, videoData);
      inputFiles.push(filename);

      console.log(`[FFmpeg.wasm] Video ${i + 1} downloaded and written to filesystem`);
    }

    // Create concat demuxer file list
    const concatList = inputFiles.map(file => `file '${file}'`).join('\n');
    await ffmpeg.writeFile('concat_list.txt', new TextEncoder().encode(concatList));

    console.log('[FFmpeg.wasm] Created concat list:', concatList);

    // Run FFmpeg concatenation command
    console.log('[FFmpeg.wasm] Starting video concatenation...');
    await ffmpeg.exec([
      '-f', 'concat',
      '-safe', '0',
      '-i', 'concat_list.txt',
      '-c', 'copy',
      'output.mp4'
    ]);

    console.log('[FFmpeg.wasm] Concatenation completed!');

    // Read the output file
    const outputData = await ffmpeg.readFile('output.mp4');

    // Create blob and URL
    const videoBlob = new Blob([outputData], { type: 'video/mp4' });
    const videoUrl = URL.createObjectURL(videoBlob);

    console.log('[FFmpeg.wasm] Stitched video created successfully!');
    console.log('[FFmpeg.wasm] Output size:', (videoBlob.size / 1024 / 1024).toFixed(2), 'MB');

    return {
      videoUrl: videoUrl,
      blob: videoBlob,
      size: videoBlob.size,
      filename: options.outputFilename || `stitched-video-${Date.now()}.mp4`,
      stitched: true
    };

  } catch (error) {
    console.error('[FFmpeg.wasm] Error:', error);
    throw new Error(`FFmpeg.wasm stitching failed: ${error.message}`);
  }
  */
}

// Main stitching function - tries FFmpeg.wasm first, falls back to sequence
export async function stitchVideos(videoUrls, options = {}) {
  console.log('[Video Stitching] Starting video combination for', videoUrls.length, 'videos');

  if (videoUrls.length < 2) {
    throw new Error('At least 2 videos required for stitching');
  }

  try {
    // Try FFmpeg.wasm for real stitching
    console.log('[Video Stitching] Attempting browser-based FFmpeg stitching...');
    return await stitchVideosFFmpegWasm(videoUrls, options);

  } catch (ffmpegError) {
    console.warn('[Video Stitching] FFmpeg.wasm failed, falling back to sequence mode:', ffmpegError.message);

    // Fallback: Use first video as representative
    const mainVideoUrl = videoUrls[0];

    console.log('[Video Stitching] Using fallback sequence mode');
    console.log('[Video Stitching] Main video:', mainVideoUrl);

    return {
      videoUrl: mainVideoUrl,
      sequenceVideos: videoUrls,
      sequenceLength: videoUrls.length,
      estimatedDuration: videoUrls.length * 5, // 5 seconds per segment
      filename: options.outputFilename || `video-sequence-${Date.now()}.mp4`,
      isSequence: true,
      stitched: false,
      fallback: true,
      note: `FFmpeg stitching failed (${ffmpegError.message}). Using first video segment. Individual segments available for download.`
    };
  }
}


